import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { UiModule } from '@app/ui';

export interface ReviewTag {
  key: string;
  description: string;
  category: 'cleanliness' | 'respect_house_rules' | 'communication';
  type: 'positive' | 'negative';
}

@Component({
  selector: 'sbnb-review-tags',
  templateUrl: './review-tags.component.html',
  styleUrls: ['./review-tags.component.scss'],
  standalone: true,
  imports: [CommonModule, UiModule],
})
export class ReviewTagsComponent implements OnChanges {
  @Input() category: 'cleanliness' | 'respect_house_rules' | 'communication';
  @Input() type: 'positive' | 'negative' | 'all' = 'all';
  @Input() selectedTags: string[] = [];
  @Input() disabled = false;
  @Input() otherReason = '';
  @Output() tagsChange = new EventEmitter<{ tags: string[]; otherReason: string; }>();

  public placeholder: string = '';

  ngOnChanges(changes: SimpleChanges): void {
    // We want to filter out the selectedTags that no longer exists
    // because the `type` was changed
    // I.e. a user selects a subset of tags, then this.type changes,
    // set becomes non-visible AND needs to be removed from the selectedTags
    if (changes['type'] && !changes['type'].firstChange) {
      setTimeout(() => {
        const validTags = this.selectedTags.filter((tagKey) => {
          const tag = this.allTags.find((t) => t.key === tagKey);

          return tag && (this.type === 'all' || tag.type === this.type);
        });

        this.emitTagsChange(validTags, '');
      }, 5);
    }

    if (changes['category']) {
      this.placeholder = this.getPlaceholderForCategory(this.category);
    }
  }

  public readonly allTags: ReviewTag[] = [
    // Cleanliness tags
    {
      key: 'host_review_guest_positive_neat_and_tidy',
      description: 'Neat & tidy',
      category: 'cleanliness',
      type: 'positive',
    },
    {
      key: 'host_review_guest_positive_kept_in_good_condition',
      description: 'Kept in good condition',
      category: 'cleanliness',
      type: 'positive',
    },
    {
      key: 'host_review_guest_positive_took_care_of_garbage',
      description: 'Took care of garbage',
      category: 'cleanliness',
      type: 'positive',
    },
    {
      key: 'host_review_guest_negative_ignored_checkout_directions',
      description: 'Ignored check-out directions',
      category: 'cleanliness',
      type: 'negative',
    },
    {
      key: 'host_review_guest_negative_garbage',
      description: 'Excessive garbage',
      category: 'cleanliness',
      type: 'negative',
    },
    {
      key: 'host_review_guest_negative_messy_kitchen',
      description: 'Messy kitchen',
      category: 'cleanliness',
      type: 'negative',
    },
    {
      key: 'host_review_guest_negative_damage',
      description: 'Damaged property',
      category: 'cleanliness',
      type: 'negative',
    },
    {
      key: 'host_review_guest_negative_ruined_bed_linens',
      description: 'Ruined bed linens',
      category: 'cleanliness',
      type: 'negative',
    },
    {
      key: 'cleanliness_other',
      description: 'Other',
      category: 'cleanliness',
      type: 'negative',
    },

    // House Rules tags
    {
      key: 'host_review_guest_negative_arrived_early',
      description: 'Arrived too early',
      category: 'respect_house_rules',
      type: 'negative',
    },
    {
      key: 'host_review_guest_negative_stayed_past_checkout',
      description: 'Stayed past checkout',
      category: 'respect_house_rules',
      type: 'negative',
    },
    {
      key: 'host_review_guest_negative_unapproved_guests',
      description: 'Unapproved guests',
      category: 'respect_house_rules',
      type: 'negative',
    },
    {
      key: 'host_review_guest_negative_unapproved_pet',
      description: 'Unapproved pet',
      category: 'respect_house_rules',
      type: 'negative',
    },
    {
      key: 'host_review_guest_negative_did_not_respect_quiet_hours',
      description: "Didn't respect quiet hours",
      category: 'respect_house_rules',
      type: 'negative',
    },
    {
      key: 'host_review_guest_negative_unapproved_filming',
      description: 'Unapproved filming or photography',
      category: 'respect_house_rules',
      type: 'negative',
    },
    {
      key: 'host_review_guest_negative_unapproved_event',
      description: 'Unapproved event',
      category: 'respect_house_rules',
      type: 'negative',
    },
    {
      key: 'host_review_guest_negative_smoking',
      description: 'Smoking',
      category: 'respect_house_rules',
      type: 'negative',
    },
    {
      key: 'respect_house_rules_other',
      description: 'Other',
      category: 'respect_house_rules',
      type: 'negative',
    },

    // Communication tags
    {
      key: 'host_review_guest_positive_helpful_messages',
      description: 'Helpful messages',
      category: 'communication',
      type: 'positive',
    },
    {
      key: 'host_review_guest_positive_respectful',
      description: 'Respectful',
      category: 'communication',
      type: 'positive',
    },
    {
      key: 'host_review_guest_positive_always_responded',
      description: 'Always responded',
      category: 'communication',
      type: 'positive',
    },
    {
      key: 'host_review_guest_negative_unhelpful_messages',
      description: 'Unhelpful responses',
      category: 'communication',
      type: 'negative',
    },
    {
      key: 'host_review_guest_negative_disrespectful',
      description: 'Disrespectful',
      category: 'communication',
      type: 'negative',
    },
    {
      key: 'host_review_guest_negative_unreachable',
      description: 'Unreachable',
      category: 'communication',
      type: 'negative',
    },
    {
      key: 'host_review_guest_negative_slow_responses',
      description: 'Slow responses',
      category: 'communication',
      type: 'negative',
    },
    {
      key: 'communication_other',
      description: 'Other',
      category: 'communication',
      type: 'negative',
    },
  ];

  public get tags(): ReviewTag[] {
    return this.allTags.filter(
      (tag) => tag.category === this.category && (this.type === 'all' || this.type === tag.type)
    );
  }

  public get isOtherSelected(): boolean {
    const otherTagKeys = ['cleanliness_other', 'respect_house_rules_other', 'communication_other'];

    return this.selectedTags.some(tagKey => otherTagKeys.includes(tagKey));
  }

  public isSelected(tagKey: string): boolean {
    return this.selectedTags.includes(tagKey);
  }

  public toggleTag(tagKey: string): void {
    if (this.disabled) return;

    const updatedTags = new Set(this.selectedTags);
    const otherTagKeys = ['cleanliness_other', 'respect_house_rules_other', 'communication_other'];

    if (this.isSelected(tagKey)) {
      updatedTags.delete(tagKey);
    } else {
      updatedTags.add(tagKey);
    }

    // If switching away from "other" tags, reset the other reason
    const wasOtherSelected = this.isOtherSelected;
    const willBeOtherSelected = Array.from(updatedTags).some(tag => otherTagKeys.includes(tag));

    let newOtherReason = this.otherReason;
    if (wasOtherSelected && !willBeOtherSelected) {
      newOtherReason = '';
    }

    this.emitTagsChange(Array.from(updatedTags), newOtherReason);
  }

  public onOtherReasonChange(reason: string): void {
    this.emitTagsChange(this.selectedTags, reason);
  }

  private emitTagsChange(tags: string[], otherReason: string = this.otherReason): void {
    const otherTagKeys = ['cleanliness_other', 'respect_house_rules_other', 'communication_other'];

    this.tagsChange.emit({
      tags,
      otherReason,
    });
  }

  private getPlaceholderForCategory(category: string): string {
    switch (category) {
      case 'cleanliness':
        return 'Appliances left dirty';
      case 'respect_house_rules':
        return 'Disabled smoke detectors';
      case 'communication':
        return 'Did not report problems during stay';
      default:
        return '';
    }
  }
}
